import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { format } from 'date-fns'

import { TaskForm } from '@containers/Forms/Task'
import { TTaskForm } from '@containers/Forms/Task/schema'
import { useAlerts } from '@hooks/alerts'
import { useDate } from '@hooks/dates'
import { useFindJob } from '@hooks/jobs'
import { useAddTask } from '@hooks/task/post'
import { IPostTask } from '@interfaces/task/downstream/post/IPostTask'

import { useAddTaskContext } from './AddTaskContext'
import { useStartDateAndTime } from '../startDateAndTime'
import { ListDummyAssignments } from './DummyAssignment/ListDummyAssignments.tsx'

export function AddTaskFormScreen() {
  const { assignmentsData, taskFormData, setTaskFormData } =
    useAddTaskContext()
  const { showDismissAlert } = useAlerts()
  const { mergeDateAndTime } = useStartDateAndTime()
  const { formatDate } = useDate()
  const { mutateAsync: postTask } = useAddTask()

  const location = useLocation()
  const jobId = (location.state as { jobId?: number })?.jobId

  const { data: rawJob } = useFindJob(Number(jobId))
  const job = rawJob ?? ({} as NonNullable<typeof rawJob>)

  const navigate = useNavigate()

  function handleNavigateAddAssignment(duration: number, currentValues: TTaskForm) {
    setTaskFormData(currentValues)
    navigate(`add-dummy-assignment/${duration}`)
  }

  function createTaskPayload(partialTask: TTaskForm) {
    const taskWithFormattedDateAndTime = formatTaskStartTimeAndDate(partialTask)
    const taskWithNormalizedPermit = formatTaskPermitData(
      taskWithFormattedDateAndTime,
    )
    const taskPayloadWithJobData = addJobDataToTaskPayload(
      taskWithNormalizedPermit,
    )
    const taskPayloadWithPostData = addPostDataToTaskPayload(
      taskPayloadWithJobData,
    )

    const taskPayloadWithCrewSize = getTotalCrewSize(taskPayloadWithPostData)
    const task = addAssignmentsDataToTaskPayload(taskPayloadWithCrewSize)

    return task as IPostTask
  }

  function formatTaskStartTimeAndDate(taskData: TTaskForm) {
    const { startTime, selectedDate, ...restData } = taskData

    return {
      ...restData,
      startTime: new Date(
        mergeDateAndTime({ startTime, selectedDate: selectedDate }),
      ),
      selectedDate: format(selectedDate, 'yyyy-MM-dd'),
    }
  }

  function formatTaskPermitData(taskData: Partial<IPostTask>) {
    const { permitTypeIds, permitStatus, permitNeeded } = taskData

    return {
      ...taskData,
      permitTypeIds: permitNeeded === 1 ? permitTypeIds : [],
      permitStatus: permitNeeded === 1 ? permitStatus : 2,
      permitNeeded,
    }
  }

  function addAssignmentsDataToTaskPayload(task: Partial<IPostTask>) {
    const assignments = assignmentsData.map((a) => {
      const { assigneeIds, dummyAssignmentId, startTime, ...rest } = a

      const partialAssignment = assigneeIds && assigneeIds.length > 0 ? a : rest

      const startTimeOption = getStartTimeOption(startTime)

      return {
        ...partialAssignment,
        startTimeOption,
        offset: 0,
      }
    })

    return {
      ...task,
      assignments,
    }
  }

  function getStartTimeOption(startTime: Date) {
    const description = formatDate(startTime).time

    return {
      description: description ?? '',
      disabled: false,
      id: 1,
      isActive: true,
      time: 0,
    }
  }

  function addPostDataToTaskPayload(task: Partial<IPostTask>) {
    const { operationPostId, ...restTask } = task
    return operationPostId ? { ...restTask, operationPostId } : restTask
  }

  function addJobDataToTaskPayload(task: Partial<IPostTask>) {
    return {
      ...task,
      job: {
        ...job,
      },
      jobId: Number(jobId),
      jobName: job.name,
    }
  }

  function handleAddTask(task: TTaskForm) {
    if (assignmentsData.length === 0) showAlertIfNoAssignments()
    else {
      const taskPayload = createTaskPayload(task)
      postTask(taskPayload)
      navigate(-1)
    }
  }

  function getTotalCrewSize(taskData: Partial<IPostTask>) {
    const crewSize = assignmentsData.reduce(
      (acc, { crewSize }) => acc + crewSize,
      0,
    )

    return {
      ...taskData,
      crewSize,
    }
  }

  function showAlertIfNoAssignments() {
    window.alert('At least one assignment is required to create a task.')
  }

  function handleShowAlertOnAddTaskDismiss() {
    showDismissAlert(() => navigate(-1))
  }

  return (
    <TaskForm
      onSave={handleAddTask}
      onClose={handleShowAlertOnAddTaskDismiss}
      title="Add Task"
      onAddAssignment={handleNavigateAddAssignment}
      task={taskFormData as never}
      isFormPrefilled={!!taskFormData}
    >
      <ListDummyAssignments />
    </TaskForm>
  )
}
